/**
 * Повторяющиеся задачи.
 *
 * Повторение реализовано как **цепочка обычных задач**, а не как одна запись
 * с бесконечным расписанием. Закрыли текущую — родилась следующая.
 *
 * Почему так, а не «виртуальные экземпляры по правилу»:
 *  - история остаётся честной: видно, что и когда реально сделано, включая
 *    задачи, закрытые с опозданием или перенесённые;
 *  - незакрытое повторение не размножается: если человек неделю не поливал
 *    цветы, он увидит одну задачу, а не семь;
 *  - весь остальной код (бюджет дня, календарь, авто-перенос) работает с
 *    обычными задачами и ничего не знает про повторения.
 *
 * Модуль содержит только чистые функции — они не трогают базу и полностью
 * покрыты тестами.
 */

import type { RecurrenceAnchor, RecurrenceType, Task } from '@/shared/db/schema';
import { addDays, diffDays, type IsoDate, weekdayIndex } from '@/shared/lib/datetime';
import { describeWeekdays, EVERY_DAY_MASK, hasWeekday } from '@/shared/lib/weekdays';

export interface RecurrenceRule {
  type: RecurrenceType;
  /** Шаг: «раз в 3 дня» → 3. Игнорируется для `weekdays`. */
  interval: number;
  /** Маска дней недели для `weekdays`: бит 0 = понедельник. */
  weekdays: number;
  from: RecurrenceAnchor;
  until: IsoDate | null;
}

export const DEFAULT_RECURRENCE: RecurrenceRule = {
  type: 'none',
  interval: 1,
  weekdays: 0,
  from: 'planned',
  until: null,
};

/** Достаёт правило из задачи, подставляя значения по умолчанию. */
export function ruleOf(task: Task): RecurrenceRule {
  return {
    type: task.recurrence ?? 'none',
    interval: Math.max(1, task.recurrenceInterval || 1),
    weekdays: task.recurrenceWeekdays || 0,
    from: task.recurrenceFrom ?? 'planned',
    until: task.recurrenceUntil ?? null,
  };
}

export function isRecurring(task: Task): boolean {
  return (task.recurrence ?? 'none') !== 'none';
}

/**
 * Дата следующего повторения после `from`.
 *
 * Возвращает `null`, если правило выключено, некорректно (например, тип
 * «по дням недели» без единого выбранного дня) или серия уже закончилась.
 */
export function nextOccurrence(rule: RecurrenceRule, from: IsoDate): IsoDate | null {
  const next = computeNext(rule, from);
  if (next === null) return null;
  if (rule.until !== null && next > rule.until) return null;
  return next;
}

function computeNext(rule: RecurrenceRule, from: IsoDate): IsoDate | null {
  const step = Math.max(1, Math.floor(rule.interval));

  switch (rule.type) {
    case 'none':
      return null;

    case 'daily':
      return addDays(from, 1);

    case 'everyNDays':
      return addDays(from, step);

    case 'everyNWeeks':
      return addDays(from, step * 7);

    case 'weekdays':
      return nextWeekday(rule.weekdays, from);

    case 'monthly':
      return addMonthsClamped(from, step);

    case 'lastDayOfMonth':
      return nextMonthEnd(from, step);
  }
}

/**
 * День задачи с учётом правила повторения.
 *
 * «В последний день месяца» — это конец месяца, а не тот день, когда задачу
 * завели: иначе первый раз пришёлся бы на день создания, и только следующие —
 * на последние дни. Одна функция на обе формы, где заводят задачи, — в списке
 * и в календаре.
 */
export function anchorDate(type: RecurrenceType, date: IsoDate): IsoDate {
  return type === 'lastDayOfMonth' ? monthEnd(date) : date;
}

/** Последний день месяца, в котором лежит дата. */
export function monthEnd(date: IsoDate): IsoDate {
  const [year, month] = date.split('-').map(Number);
  const end = new Date(year ?? 1970, month ?? 1, 0);
  return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
}

/**
 * Следующий конец месяца.
 *
 * Из середины месяца — конец этого же месяца: задача, поставленная на десятое
 * с правилом «в последний день», должна повториться тридцатого, а не через
 * полтора месяца. Из самого конца — конец следующего, и так без сдвига: в
 * отличие от «раз в месяц», короткий февраль не утягивает за собой остальные
 * месяцы.
 */
function nextMonthEnd(from: IsoDate, months: number): IsoDate {
  const current = monthEnd(from);
  if (current > from) return current;

  const [year, month] = from.split('-').map(Number);
  const target = new Date(year ?? 1970, (month ?? 1) - 1 + months, 1);
  return monthEnd(
    `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-01`,
  );
}

/**
 * Ближайший следующий день из маски дней недели.
 * Проверяются семь дней вперёд — этого достаточно, чтобы обойти неделю
 * целиком и вернуться к исходному дню.
 */
function nextWeekday(mask: number, from: IsoDate): IsoDate | null {
  if ((mask & 0b1111111) === 0) return null;

  for (let shift = 1; shift <= 7; shift += 1) {
    const candidate = addDays(from, shift);
    if (hasWeekday(mask, weekdayIndex(candidate))) return candidate;
  }
  return null;
}

/**
 * Прибавляет месяцы, удерживая число месяца.
 *
 * 31 января + 1 месяц даёт 28 (или 29) февраля, а не 3 марта: для
 * ежемесячных дел «последнее число» ожидаемее, чем переход через границу.
 */
function addMonthsClamped(from: IsoDate, months: number): IsoDate {
  const [year, month, day] = from.split('-').map(Number);
  const base = new Date(year ?? 1970, (month ?? 1) - 1 + months, 1);
  const daysInTargetMonth = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
  base.setDate(Math.min(day ?? 1, daysInTargetMonth));

  const y = base.getFullYear();
  const m = String(base.getMonth() + 1).padStart(2, '0');
  const d = String(base.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Дата следующего экземпляра при закрытии задачи.
 *
 * Точка отсчёта зависит от правила: для жёсткого ритма («аренда 5-го числа»)
 * считаем от плановой даты, чтобы пропуск не сдвигал график; для дел вида
 * «раз в 3 дня» — от даты выполнения, потому что отсчёт идёт от последнего
 * раза, а не от того, когда это планировалось.
 *
 * Если считаем от плановой даты и она давно в прошлом, повторения
 * прокручиваются до ближайшего будущего: иначе после долгого перерыва
 * пользователь получил бы задачу задним числом.
 */
export function nextDateAfterCompletion(
  rule: RecurrenceRule,
  plannedDate: IsoDate | null,
  completedDate: IsoDate,
): IsoDate | null {
  if (rule.type === 'none') return null;

  if (rule.from === 'completed' || plannedDate === null) {
    return nextOccurrence(rule, completedDate);
  }

  let candidate = nextOccurrence(rule, plannedDate);
  // Ограничение на число шагов защищает от зацикливания при некорректном
  // правиле, которое не двигает дату вперёд.
  let guard = 0;
  while (candidate !== null && diffDays(completedDate, candidate) <= 0 && guard < 400) {
    candidate = nextOccurrence(rule, candidate);
    guard += 1;
  }
  return candidate;
}

/** Человеческое описание правила — для карточки задачи и формы. */
export function describeRecurrence(rule: RecurrenceRule): string {
  const step = Math.max(1, Math.floor(rule.interval));

  switch (rule.type) {
    case 'none':
      return 'Не повторяется';
    case 'daily':
      return 'Каждый день';
    case 'everyNDays':
      return step === 1 ? 'Каждый день' : `Раз в ${step} ${pluralDays(step)}`;
    case 'everyNWeeks':
      return step === 1 ? 'Каждую неделю' : `Раз в ${step} ${pluralWeeks(step)}`;
    case 'monthly':
      return step === 1 ? 'Каждый месяц' : `Раз в ${step} ${pluralMonths(step)}`;
    case 'lastDayOfMonth':
      return step === 1
        ? 'В последний день месяца'
        : `В последний день раз в ${step} ${pluralMonths(step)}`;
    case 'weekdays':
      return describeWeekdayMask(rule.weekdays);
  }
}

function describeWeekdayMask(mask: number): string {
  const normalized = mask & EVERY_DAY_MASK;
  return normalized === 0 ? 'Дни не выбраны' : describeWeekdays(normalized);
}

function pluralDays(count: number): string {
  const last = count % 10;
  const teen = count % 100 > 10 && count % 100 < 20;
  if (!teen && last === 1) return 'день';
  if (!teen && last > 1 && last < 5) return 'дня';
  return 'дней';
}

function pluralWeeks(count: number): string {
  const last = count % 10;
  const teen = count % 100 > 10 && count % 100 < 20;
  if (!teen && last === 1) return 'неделю';
  if (!teen && last > 1 && last < 5) return 'недели';
  return 'недель';
}

function pluralMonths(count: number): string {
  const last = count % 10;
  const teen = count % 100 > 10 && count % 100 < 20;
  if (!teen && last === 1) return 'месяц';
  if (!teen && last > 1 && last < 5) return 'месяца';
  return 'месяцев';
}
